import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import moment from 'moment';
import { firstValueFrom } from 'rxjs';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { CustomTableReport } from 'src/app/interfaces/custom-table-report/custom-table-report.interface';
import { InventoryReportFilterModel } from 'src/app/models/inventory/inventory-report-filter.model';
import { ArticleService } from 'src/app/services/archive/article.service';
import { StoreService } from 'src/app/services/archive/store.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { InventoryReportService } from 'src/app/services/inventory/inventory-report.service';
import { InventoryService } from 'src/app/services/inventory/inventory.service';
import { BranchService } from 'src/app/services/management/branch.service';
import { RentalPeriodService } from 'src/app/services/rental/rental-period.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { CustomValidators } from 'src/app/validators/custom-validators';

@Component({
  selector: 'app-branch-average-cost',
  templateUrl: './branch-average-cost.component.html',
  styleUrls: ['./branch-average-cost.component.scss'],
  standalone: false
})
export class BranchAverageCostComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('filtersContainer') filtersContainer!: ElementRef;

  captionFilters: string[] = [];
  changeFilters: boolean = false;
  dataReport: CustomTableReport;
  filterObserver?: ResizeObserver;
  filtersHeight: number = 0;
  filteredStores: any[] = [];
  formInventoryReportFilter: FormGroup = new FormGroup({});
  inventoryId: number = 0;
  listArticle: any[] = [];
  listBranch: any[] = [];
  listInventory: any[] = [];
  listStore: any[] = [];
  nameWindow: string = 'Informe de Saldos por Sucursal Valorizados al Costo Promedio';
  projectName = ProjectName.projectName;
  storeId: number = 0;

  constructor(private articleService: ArticleService,
    private branchService: BranchService,
    private formBuilder: FormBuilder,
    private headerService: HeaderService,
    private inventoryService: InventoryService,
    private inventoryReportService: InventoryReportService,
    private rentalPeriodService: RentalPeriodService,
    private storeService: StoreService,
    private title: Title,
    private toastHandlerService: ToastHandlerService) {
    this.validateForm();
  }

  async ngOnInit() {
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.getFirstBranchByUserAuthenticatedAsync();
    await this.getAllLists();
  }

  ngAfterViewInit() {
    this.filterObserver = new ResizeObserver(() => {
      this.filtersHeight = this.filtersContainer?.nativeElement?.offsetHeight || 0;
    });
    this.filterObserver.observe(this.filtersContainer.nativeElement);
  }

  ngOnDestroy() {
    this.filterObserver?.disconnect();
  }

  changeArticle(articleId: number | null) {
    if (articleId === 0 || articleId === null || articleId === undefined) {
      this.formInventoryReportFilter.controls["articleId"].patchValue(0);
    }
    this.changeFilter();
  }

  changeInventory(inventoryId: number | null) {
    this.filterStoreByInventoryId(inventoryId);
    if (inventoryId != 0 && inventoryId != null) {
      this.formInventoryReportFilter.controls["storeId"].patchValue(0);
    } else {
      this.formInventoryReportFilter.controls["inventoryId"].patchValue(0);
      this.formInventoryReportFilter.controls["storeId"].patchValue(0);
    }
    this.changeFilter();
  }

  changeFilter() {
    this.changeFilters = false;
  }

  changeStore(storeId: number | null) {
    if (storeId === 0 || storeId === null || storeId === undefined) {
      this.formInventoryReportFilter.controls["storeId"].patchValue(0);
    }
    this.changeFilter();
  }


  clear() {
    this.changeFilter();
    this.validateForm();
    this.getFirstBranchByUserAuthenticatedAsync();
    this.setInventoryDefault();
  }

  async exportExcel() {
    if (this.changeFilters) {
      const dateInitial = this.formInventoryReportFilter.controls['initialDate'].value;
      const initialDate = `${dateInitial.getFullYear()}-${(dateInitial.getMonth() + 1).toString().padStart(2, '0')}`;
      const period = initialDate.slice(5);
      const year = initialDate.slice(0, -3);
      try {
        const modelInventoryReportFilter = this.formInventoryReportFilter.value as InventoryReportFilterModel;
        modelInventoryReportFilter.year = parseInt(year);
        modelInventoryReportFilter.period = parseInt(period);
        const response = await firstValueFrom(this.inventoryReportService.exportInventoryReportBranchAverageCost(modelInventoryReportFilter));
        const blob = response;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Informe de Saldos por Sucursal Valorizados al Costo Promedio.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error exporting report.', error);
      }
    } else {
      this.toastHandlerService.showToast('info', 'No se encontraron datos para exportar.');
    }
  }

  filterStoreByInventoryId(inventoryId: number | null) {
    const rowsFiltered = this.listStore.filter(x => x.inventoryId === inventoryId);
    if (inventoryId != null) {
      this.filteredStores = rowsFiltered;
    } else {
      this.filteredStores = this.listStore;
    }
    this.formInventoryReportFilter.controls["storeId"].patchValue(0);
  }

  async getAllLists() {
    try {
      const [articles, branches, inventories, stores] = await Promise.all([
        firstValueFrom(this.articleService.getAllArticlesForSelectAsync()),
        firstValueFrom(this.branchService.getAllBranchesForSelectAsync()),
        firstValueFrom(this.inventoryService.getAllInventoriesForSelectAsync()),
        firstValueFrom(this.storeService.getAllStoresWithoutSubrentalForSelect())
      ]);
      this.listArticle = articles;
      this.listBranch = branches;
      this.listInventory = inventories;
      this.listStore = stores;
      this.setInventoryDefault();
      this.filteredStores = this.listStore;
      return true;
    } catch (error) {
      console.error('Error getting lists:', error);
      return false;
    }
  }

  async getFirstBranchByUserAuthenticatedAsync() {
    try {
      const branch = await firstValueFrom(this.branchService.getFirstBranchByUserAuthenticatedAsync());
      this.formInventoryReportFilter.controls["branchId"].patchValue(branch.branchId);
      this.getLastRentalPeriod(branch.branchId);
    } catch (error) {
      console.error("Error querying branch:", error);
    }
  }

  async getLastRentalPeriod(branchId: number) {
    try {
      const rentalPeriod = await firstValueFrom(this.rentalPeriodService.getLastRentalPeriod(branchId));
      this.formInventoryReportFilter.controls["initialDate"].patchValue(moment(rentalPeriod.initialDate).startOf('month').toDate());
    } catch (error) {
      console.error("Error when querying the rental period:", error);
    }
  }

  printReport() {
    if (this.changeFilters) {
      window.print();
    } else {
      this.toastHandlerService.showToast('info', 'No se encontraron datos para imprimir.');
    }
  }

  async send() {
    Object.keys(this.formInventoryReportFilter.controls).forEach(key => {
      const control = this.formInventoryReportFilter.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });
    if (this.formInventoryReportFilter.valid) {
      const dateInitial = this.formInventoryReportFilter.controls['initialDate'].value;
      if (dateInitial === '') {
        this.toastHandlerService.showToast('warning', 'Debe seleccionar un período.', 'Atención.');
        return;
      }
      const initialDate = `${dateInitial.getFullYear()}-${(dateInitial.getMonth() + 1).toString().padStart(2, '0')}`;
      const period = initialDate.slice(5);
      const year = initialDate.slice(0, -3);

      const inventoryId = this.formInventoryReportFilter.controls['inventoryId'].value;
      if (inventoryId === 0 || inventoryId === null) {
        this.toastHandlerService.showToast('warning', 'Debe seleccionar un Inventario.', 'Atención.');
        return;
      }

      const modelInventoryReportFilter = this.formInventoryReportFilter.value as InventoryReportFilterModel;
      modelInventoryReportFilter.year = parseInt(year);
      modelInventoryReportFilter.period = parseInt(period);
      try {
        this.dataReport = await firstValueFrom(this.inventoryReportService.inventoryReportBranchAverageCost(modelInventoryReportFilter));
        if (this.dataReport.data != null && (this.dataReport.data.length != 0 && this.dataReport.data.length != null)) {
          this.changeFilters = true;
        } else {
          this.changeFilters = false;
          this.toastHandlerService.showToast('info', 'No se encontraron datos.');
        }
      } catch (error) {
        console.error("Error al consultar el informe:", error);
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      articleId: {
        notNumber: 'Debe seleccionar un Artículo válido.'
      },
      storeId: {
        notNumber: 'Debe seleccionar una Bodega válida.'
      }
    };

    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formInventoryReportFilter.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  setInventoryDefault() {
    const rentalInventory = this.listInventory.find(x => x.label.startsWith('1 -'));
    const inventoryId = rentalInventory ? rentalInventory.value : 0;
    this.formInventoryReportFilter.controls["inventoryId"].patchValue(inventoryId);
    this.filterStoreByInventoryId(inventoryId);
  }

  validateForm() {
    this.formInventoryReportFilter = this.formBuilder.group({
      initialDate: [''],
      branchId: [0],
      articleId: [0, [CustomValidators.numberOnlyValidator()]],
      inventoryId: [0],
      storeId: [0, [CustomValidators.numberOnlyValidator()]]
    });
  }

}
